/**
 * "What this ticket turned into" - the work filed from a ticket, on the ticket.
 *
 * The card exists because the link is otherwise one-way. A task carries the
 * ticket it came from and prints it; without this card the ticket has no idea,
 * so an operator reopening a three-day-old thread has no way to tell whether
 * somebody already made a job of it - and files a second one.
 *
 * IT SHOWS THE TASK'S OWN STATE, never a copy. Whether the work is done is a
 * fact the task register owns; a flag kept here as well would disagree with it
 * the first time somebody ticked the task off from the board. So the row reads
 * `status` straight off the task and the ticket keeps nothing of its own.
 *
 * Drawn only when there is at least one task, the same rule the call badge above
 * it follows: a card saying "no tasks" on every ticket anybody ever opened by
 * hand is four lines of nothing on almost every screen. The button that creates
 * the first one lives in the page head, where the ticket's other actions are.
 */
import { Link } from "react-router-dom";
import { ListTodo } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { TicketTask } from "../../../lib/support";
import { Card, CardHead, StatusPill, fmtDate } from "../../ui";
import { UserAvatars } from "../_shared/UserChips";
import { taskTone } from "../projects/status";

/** `crm-card` is a surface and nothing else, so a body says where its edges are. */
const BODY = "px-6 pb-5 pt-3";

export function TicketTasks({ tasks }: { tasks: TicketTask[] }) {
  const t = useT();
  if (tasks.length === 0) return null;

  return (
    <Card>
      <CardHead
        title={
          <span className="flex items-center gap-1.5">
            <ListTodo className="size-3.5" />
            {t("support.tasksCard")}
          </span>
        }
      />
      <div className={`${BODY} flex flex-col`}>
        {tasks.map((task) => (
          <Link
            key={task.id}
            to={`/tasks/${task.id}`}
            className="flex flex-col gap-1.5 border-t border-[var(--crm-hair)] py-2.5 text-[13px] first:border-t-0 first:pt-0 hover:text-foreground"
            title={task.title}
          >
            {/* Two lines, not one. This card lives in the narrow column of the
                ticket page, and a row holding a status pill, a title, faces and a
                date side by side cuts the title to three words at that width -
                which is the one thing on the row that has to be readable. So the
                title gets the line to itself and the three short facts share the
                next one. */}
            <span className="truncate font-medium">{task.title}</span>
            <span className="flex items-center gap-2 text-[12px]">
              {/* The state first on its own line, because it is what the reader
                  came for: the question this card answers is "has somebody
                  already done something about this". */}
              <StatusPill tone={taskTone(task.status)}>{t(`tasks.status.${task.status}`)}</StatusPill>
              {/* Only when somebody is on it. `UserAvatars` draws its "nobody"
                  mark for an empty list, which holds a table row open and is a
                  stray hyphen here. */}
              {task.assignees.length ? <UserAvatars ids={task.assignees} size={18} /> : null}
              {task.dueDate ? (
                <span className="crm-mut crm-tnum crm-push">{fmtDate(task.dueDate)}</span>
              ) : null}
            </span>
          </Link>
        ))}
      </div>
    </Card>
  );
}
